import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { 
  Menu, Home, Plus, Eye, User, Settings, Users, 
  Download, Star
} from "lucide-react";
import useNavigationNotifications from "@/hooks/useNavigationNotifications";
import MarkAllReadButton from "@/components/MarkAllReadButton";
import mascot from "@/assets/bailbuddies-icon.png";

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  description?: string;
  notificationKey?: keyof ReturnType<typeof useNavigationNotifications>["notifications"];
}

interface NavSection {
  title: string;
  items: NavItem[];
}

const navSections: NavSection[] = [
  {
    title: "Main",
    items: [
      { label: "Dashboard", path: "/dashboard", icon: <Home className="w-5 h-5" />, description: "Your home base", notificationKey: "dashboard" },
      { label: "Create a Plan", path: "/create", icon: <Plus className="w-5 h-5" />, description: "Start something new" },
      { label: "Memory Vault", path: "/memory-vault", icon: <Eye className="w-5 h-5" />, description: "Revealed plans & memories", notificationKey: "vault" },
    ],
  },
  {
    title: "Profile & Social",
    items: [
      { label: "Profile", path: "/profile", icon: <User className="w-5 h-5" />, description: "Your stats & info", notificationKey: "profile" },
      { label: "Contacts", path: "/contacts", icon: <Users className="w-5 h-5" />, description: "Friends & circles", notificationKey: "contacts" },
      { label: "Settings", path: "/settings", icon: <Settings className="w-5 h-5" />, description: "App preferences" },
    ],
  },
  {
    title: "Other",
    items: [
      { label: "Install App", path: "/install", icon: <Download className="w-5 h-5" />, description: "Get the mobile app" },
    ],
  },
];

export const NavigationMenu = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { notifications } = useNavigationNotifications();

  const handleNavigate = (path: string) => {
    navigate(path);
    setOpen(false);
  };
  
  const getBadgeCount = (item: NavItem): number | undefined => {
    if (item.notificationKey && notifications[item.notificationKey] > 0) {
      return notifications[item.notificationKey];
    }
    return undefined;
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="rounded-full" aria-label="Open navigation menu">
          <Menu className="w-5 h-5" />
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-80 p-0">
        <SheetHeader className="p-4 pb-2 bg-card">
          <SheetTitle className="flex items-center gap-3">
            <img src={mascot} alt="BailBuddies" className="w-8 h-8" />
            <span className="font-display bg-primary bg-clip-text text-transparent">
              BailBuddies
            </span>
          </SheetTitle>
          <div className="flex items-center justify-end mt-3">
            <MarkAllReadButton />
          </div>
        </SheetHeader>
        
        <ScrollArea className="h-[calc(100vh-130px)]">
          <div className="p-4 space-y-6">
            {navSections.map((section) => (
              <div key={section.title}>
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                  {section.title}
                </h3>
                <div className="space-y-1">
                  {section.items.map((item) => {
                    const isActive = location.pathname === item.path;
                    const badgeCount = getBadgeCount(item);
                    return (
                      <button
                        key={`${item.path}-${item.label}`}
                        onClick={() => handleNavigate(item.path)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all ${
                          isActive ? "bg-primary/10 text-primary" : "hover:bg-muted/50"
                        }`}
                      >
                        <div className={`p-1.5 rounded-lg ${isActive ? "bg-primary/20" : "bg-muted"}`}>
                          {item.icon}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium truncate">{item.label}</span>
                            {badgeCount && badgeCount > 0 && (
                              <span className="min-w-[18px] h-[18px] flex items-center justify-center bg-destructive text-destructive-foreground text-[10px] font-bold rounded-full px-1 flex-shrink-0">
                                {badgeCount > 99 ? "99+" : badgeCount}
                              </span>
                            )}
                          </div>
                          {item.description && (
                            <p className="text-xs text-muted-foreground truncate">{item.description}</p>
                          )}
                        </div>
                        {isActive && <Star className="w-4 h-4 text-primary fill-primary flex-shrink-0" />}
                      </button>
                    );
                  })}
                </div>
                <Separator className="mt-4" />
              </div>
            ))}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};

export default NavigationMenu;
